'use client';

import { createClient } from '@/lib/supabase/client';

export interface PropertyCollection {
  id: string;
  userId: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  isPublic: boolean;
  shareToken: string;
  createdAt: string;
  updatedAt: string;
  propertyCount?: number;
}

export interface CollectionProperty {
  id: string;
  collectionId: string;
  userId: string;
  propertyId: string;
  propertyTitle: string;
  propertyPrice: string;
  propertyLocation: string;
  propertyImage: string;
  propertyType: string;
  listingType: string;
  note: string;
  createdAt: string;
  updatedAt: string;
}

export interface SavePropertyInput {
  propertyId: string;
  propertyTitle: string;
  propertyPrice: string;
  propertyLocation: string;
  propertyImage?: string;
  propertyType?: string;
  listingType?: string;
}

function toCollection(row: any): PropertyCollection {
  return {
    id: row.id,
    userId: row.user_id,
    name: row.name,
    description: row.description || '',
    color: row.color || '#3b82f6',
    icon: row.icon || 'folder',
    isPublic: row.is_public || false,
    shareToken: row.share_token || '',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    propertyCount: row.property_count ?? row.collection_properties?.length ?? 0,
  };
}

function toCollectionProperty(row: any): CollectionProperty {
  return {
    id: row.id,
    collectionId: row.collection_id,
    userId: row.user_id,
    propertyId: row.property_id,
    propertyTitle: row.property_title || '',
    propertyPrice: row.property_price || '',
    propertyLocation: row.property_location || '',
    propertyImage: row.property_image || '',
    propertyType: row.property_type || '',
    listingType: row.listing_type || '',
    note: row.note || '',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const collectionsService = {
  async getUserCollections(): Promise<PropertyCollection[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('property_collections')
      .select('*, collection_properties(id)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true });

    if (error) {
      console.log('Collections fetch error:', error.message);
      return [];
    }

    return (data || []).map((row: any) => ({
      ...toCollection(row),
      propertyCount: row.collection_properties?.length ?? 0,
    }));
  },

  async createCollection(input: {
    name: string;
    description?: string;
    color?: string;
    icon?: string;
  }): Promise<PropertyCollection | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('property_collections')
      .insert({
        user_id: user.id,
        name: input.name,
        description: input.description || '',
        color: input.color || '#3b82f6',
        icon: input.icon || 'folder',
      })
      .select()
      .single();

    if (error) {
      console.log('Create collection error:', error.message);
      return null;
    }

    return toCollection(data);
  },

  async updateCollection(id: string, input: {
    name?: string;
    description?: string;
    color?: string;
    icon?: string;
    isPublic?: boolean;
  }): Promise<PropertyCollection | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const updateData: any = {};
    if (input.name !== undefined) updateData.name = input.name;
    if (input.description !== undefined) updateData.description = input.description;
    if (input.color !== undefined) updateData.color = input.color;
    if (input.icon !== undefined) updateData.icon = input.icon;
    if (input.isPublic !== undefined) updateData.is_public = input.isPublic;

    const { data, error } = await supabase
      .from('property_collections')
      .update(updateData)
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      console.log('Update collection error:', error.message);
      return null;
    }

    return toCollection(data);
  },

  async deleteCollection(id: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { error } = await supabase
      .from('property_collections')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.log('Delete collection error:', error.message);
      return false;
    }

    return true;
  },

  async getCollectionProperties(collectionId: string): Promise<CollectionProperty[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('collection_properties')
      .select('*')
      .eq('collection_id', collectionId)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.log('Collection properties fetch error:', error.message);
      return [];
    }

    return (data || []).map(toCollectionProperty);
  },

  async savePropertyToCollection(
    collectionId: string,
    property: SavePropertyInput,
    note?: string
  ): Promise<CollectionProperty | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('collection_properties')
      .upsert({
        collection_id: collectionId,
        user_id: user.id,
        property_id: property.propertyId,
        property_title: property.propertyTitle,
        property_price: property.propertyPrice,
        property_location: property.propertyLocation,
        property_image: property.propertyImage || '',
        property_type: property.propertyType || '',
        listing_type: property.listingType || '',
        note: note || '',
      }, { onConflict: 'collection_id,property_id' })
      .select()
      .single();

    if (error) {
      console.log('Save property error:', error.message);
      return null;
    }

    return toCollectionProperty(data);
  },

  async removePropertyFromCollection(collectionId: string, propertyId: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { error } = await supabase
      .from('collection_properties')
      .delete()
      .eq('collection_id', collectionId)
      .eq('property_id', propertyId)
      .eq('user_id', user.id);

    if (error) {
      console.log('Remove property error:', error.message);
      return false;
    }

    return true;
  },

  async updatePropertyNote(collectionId: string, propertyId: string, note: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { error } = await supabase
      .from('collection_properties')
      .update({ note })
      .eq('collection_id', collectionId)
      .eq('property_id', propertyId)
      .eq('user_id', user.id);

    if (error) {
      console.log('Update note error:', error.message);
      return false;
    }

    return true;
  },

  async getPropertyCollections(propertyId: string): Promise<string[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('collection_properties')
      .select('collection_id')
      .eq('property_id', propertyId)
      .eq('user_id', user.id);

    if (error) {
      console.log('Get property collections error:', error.message);
      return [];
    }

    return (data || []).map((row: any) => row.collection_id);
  },

  async getSharedCollection(shareToken: string): Promise<{ collection: PropertyCollection; properties: CollectionProperty[] } | null> {
    const supabase = createClient();

    const { data: collectionData, error: collectionError } = await supabase
      .from('property_collections')
      .select('*')
      .eq('share_token', shareToken)
      .eq('is_public', true)
      .maybeSingle();

    if (collectionError || !collectionData) return null;

    const { data: propertiesData, error: propertiesError } = await supabase
      .from('collection_properties')
      .select('*')
      .eq('collection_id', collectionData.id)
      .order('created_at', { ascending: false });

    if (propertiesError) return null;

    return {
      collection: toCollection(collectionData),
      properties: (propertiesData || []).map(toCollectionProperty),
    };
  },
};
